<template>
    <div>
        <template v-if="hasPlanDowngrade">
            Your account will be downgraded from the Pro ({{
                $filters.number(subscription.plan_credits)
            }}
            credits) ({{ defaultPlanName }}) to the Pro ({{
                $filters.number(currentPlan.credits)
            }}
            credits) ({{ target }}) plan at the end of your billing cycle on
            {{ currentDateFormatted }}
        </template>
        <template v-else>
            <form-message v-if="form.errors.has('stripe')" :message="form.errors.get('stripe')" />
            <form-message
                v-else-if="form.errors.get('card_error.0')"
                :message="form.errors.get('card_error.0')"
            />
            <form-message
                v-else-if="form.errors.get('error')"
                :message="form.errors.get('error')"
            />
            <form-message
                v-else-if="!form.errors.get('name') && form.errors.any()"
                message="Something went wrong, please try again."
            />

            <p v-if="!isOrganizationOwner">
                Please contact your administrator about Upgrading to another plan.
            </p>

            <article v-if="hasCoupon && !prorateLoading" class="message is-success">
                <div class="message-body">
                    <div class="message-content">
                        <div v-html="couponMessage" />
                    </div>
                </div>
            </article>

            <div v-if="hasNotCard" class="columns">
                <div class="column">
                    <div v-if="prorateLoading">
                        <div class="card-content has-text-centered" style="justify-content: center">
                            <basic-loader :center="true" aria-label="ChangePlanNew" />
                        </div>
                    </div>
                    <template v-else>
                        <form-message v-if="stripeError" :message="stripeError" />

                        <template v-if="isProrate">
                            <p class="field">
                                <b>Order summary</b>
                            </p>

                            <div class="columns is-marginless">
                                <div class="column is-9">
                                    {{ planNameWithoutCard }}
                                    <span
                                        v-if="defaultType.includes('annual')"
                                        class="upgradePlanItem__note"
                                        style="text-align: left"
                                    >
                                        <sup>*</sup>Billed annually
                                    </span>
                                </div>
                                <div class="column is-3 has-text-right">
                                    {{ $filters.currency(linesPlanAmount, 2) }}
                                </div>
                            </div>

                            <div
                                v-if="prorate.quantity !== 0"
                                class="columns is-marginless is-small-marginless"
                            >
                                <div class="column is-9">
                                    <small v-if="form.interval === 'yearly'">
                                        - Plus {{ $filters.number(prorate.quantity, 0) }} seat{{
                                            prorate.quantity > 1 ? 's' : ''
                                        }}
                                    </small>
                                    <small v-else>
                                        - Plus {{ $filters.number(prorate.quantity, 0) }} seat{{
                                            prorate.quantity > 1 ? 's' : ''
                                        }}
                                    </small>
                                </div>

                                <div class="column is-3 has-text-right">
                                    {{ $filters.currency(linesSeatAmount, 2) }}
                                </div>
                            </div>

                            <plan-tax-line :prorate="prorate" />
                            <div
                                v-if="prorate.shared_inboxes_quantity > 0"
                                class="columns is-marginless is-small-marginless"
                            >
                                <div class="column is-9">
                                    <small v-if="form.interval === 'yearly'">
                                        - Plus
                                        {{ $filters.number(prorate.shared_inboxes_quantity, 0) }}
                                        number{{ prorate.shared_inboxes_quantity > 1 ? 's' : '' }}
                                    </small>
                                    <small v-else>
                                        - Plus
                                        {{ $filters.number(prorate.shared_inboxes_quantity, 0) }}
                                        number{{ prorate.shared_inboxes_quantity > 1 ? 's' : '' }}
                                    </small>
                                </div>

                                <div class="column is-3 has-text-right">
                                    {{ $filters.currency(linesInboxAmount, 2) }}
                                </div>
                            </div>

                            <div
                                v-if="prorate.lines.find((line) => line.key === 'compliance')"
                                class="columns is-marginless is-small-marginless"
                            >
                                <div class="column is-9">
                                    <small v-if="form.interval === 'yearly'">
                                        - Plus Calling Compliance @ $120/year
                                    </small>
                                    <small v-else> - Plus Calling Compliance @ $10/month </small>
                                </div>

                                <div class="column is-3 has-text-right">
                                    {{ $filters.currency(linesComplianceAmount, 2) }}
                                </div>
                            </div>

                            <div
                                v-if="prorate.lines.find((line) => line.key === 'custom_domain')"
                                class="columns is-marginless is-small-marginless"
                            >
                                <div class="column is-9">
                                    <small> - Plus custom domain </small>
                                </div>

                                <div class="column is-3 has-text-right">
                                    {{ $filters.currency(linesCustomDomainAmount, 2) }}
                                </div>
                            </div>

                            <div v-if="hasCoupon" class="columns is-marginless">
                                <div class="column is-9">Discount (-{{ percentCoupon }})</div>
                                <div class="column is-3 has-text-right has-text-success">
                                    {{ percentDiscountTotal }}
                                </div>
                            </div>

                            <div v-if="prorate.credit_amount" class="columns is-marginless">
                                <div class="column is-9">
                                    Unused time on {{ subscriptionPlanName }} after {{ today }}:
                                </div>
                                <div class="column is-3 has-text-right has-text-success">
                                    {{ $filters.currency(prorate.credit_amount, 2) }}
                                </div>
                            </div>

                            <div class="columns upgrade-plan__amount">
                                <div class="column is-9">
                                    <strong>Total</strong>
                                </div>
                                <div class="column is-3 has-text-right">
                                    <strong>{{
                                        $filters.currency(prorate.prorated_amount, 2)
                                    }}</strong>
                                </div>
                            </div>

                            <div class="columns is-marginless card-footer">
                                <div class="column">
                                    <small>
                                        The next charge on your {{ form.interval }} billing cycle
                                        will be on {{ $filters.date(prorate.next_charge_date) }}.
                                    </small>
                                </div>
                            </div>

                            <hr />
                        </template>
                        <template v-else>
                            <p class="field">
                                <b>Order summary</b>
                            </p>
                            <p class="field">
                                {{ planNameWithoutCard }}
                            </p>
                            <span
                                v-if="defaultType.includes('annual')"
                                class="upgradePlanItem__note"
                                style="text-align: left"
                            >
                                <sup>*</sup>Billed annually
                            </span>

                            <hr />
                        </template>

                        <p class="field">
                            <b>Add Payment Information</b>
                        </p>

                        <form-input
                            v-model="form.name"
                            label="Card Holder Name"
                            placeholder="Card Holder Name"
                            :error="form.errors.get('name')"
                            @input="form.errors.clear('name')"
                        />

                        <div
                            class="field"
                            :style="{
                                opacity: !prorateLoading ? 1 : 0,
                                height: !prorateLoading ? 'auto' : 0,
                            }"
                        >
                            <label class="label"> Card Details </label>
                            <div class="control">
                                <div id="create-card" class="input" />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div v-else class="columns">
                <div class="column">
                    <div v-if="prorateLoading">
                        <div class="card-content has-text-centered" style="justify-content: center">
                            <basic-loader :center="true" aria-label="ChangePlanNew" />
                        </div>
                    </div>
                    <div v-else class="upgrade-plan__modal">
                        <div class="column is-12">
                            <b>Order summary</b>
                        </div>

                        <div class="columns is-marginless">
                            <div class="column is-9">
                                {{ planName }} - {{ $filters.currency(linesPlanAmount, 2) }} /
                                {{ typeName }}
                            </div>
                            <div class="column is-3 has-text-right">
                                {{ $filters.currency(linesPlanAmount, 2) }}
                            </div>
                        </div>

                        <div
                            v-if="prorate.quantity !== 0"
                            class="columns is-marginless is-small-marginless"
                        >
                            <div class="column is-9">
                                <small v-if="form.interval === 'yearly'">
                                    - Plus {{ $filters.number(prorate.quantity, 0) }} seat{{
                                        prorate.quantity > 1 ? 's' : ''
                                    }}
                                </small>
                                <small v-else>
                                    - Plus {{ $filters.number(prorate.quantity, 0) }} seat{{
                                        prorate.quantity > 1 ? 's' : ''
                                    }}
                                </small>
                            </div>

                            <div class="column is-3 has-text-right">
                                {{ $filters.currency(linesSeatAmount, 2) }}
                            </div>
                        </div>

                        <plan-tax-line :prorate="prorate" />
                        <div
                            v-if="prorate.shared_inboxes_quantity > 0"
                            class="columns is-marginless is-small-marginless"
                        >
                            <div class="column is-9">
                                <small v-if="form.interval === 'yearly'">
                                    - Plus
                                    {{ $filters.number(prorate.shared_inboxes_quantity, 0) }}
                                    number{{ prorate.shared_inboxes_quantity > 1 ? 's' : '' }}
                                </small>
                                <small v-else>
                                    - Plus
                                    {{ $filters.number(prorate.shared_inboxes_quantity, 0) }}
                                    number{{ prorate.shared_inboxes_quantity > 1 ? 's' : '' }}
                                </small>
                            </div>

                            <div class="column is-3 has-text-right">
                                {{ $filters.currency(linesInboxAmount, 2) }}
                            </div>
                        </div>

                        <div
                            v-if="prorate.lines.find((line) => line.key === 'compliance')"
                            class="columns is-marginless is-small-marginless"
                        >
                            <div class="column is-9">
                                <small v-if="form.interval === 'yearly'">
                                    - Plus Calling Compliance @ $120/year
                                </small>
                                <small v-else> - Plus Calling Compliance @ $10/month </small>
                            </div>

                            <div class="column is-3 has-text-right">
                                {{ $filters.currency(linesComplianceAmount, 2) }}
                            </div>
                        </div>

                        <div
                            v-if="prorate.lines.find((line) => line.key === 'custom_domain')"
                            class="columns is-marginless is-small-marginless"
                        >
                            <div class="column is-9">
                                <small> - Plus custom domain </small>
                            </div>

                            <div class="column is-3 has-text-right">
                                {{ $filters.currency(linesCustomDomainAmount, 2) }}
                            </div>
                        </div>

                        <div v-if="hasCoupon" class="columns is-marginless">
                            <div class="column is-9">Discount (-{{ percentCoupon }})</div>
                            <div class="column is-3 has-text-right has-text-success">
                                {{ percentDiscountTotal }}
                            </div>
                        </div>

                        <div v-if="prorate.credit_amount" class="columns is-marginless">
                            <div class="column is-9">
                                Unused time on {{ subscriptionPlanName }} after {{ today }}:
                            </div>
                            <div class="column is-3 has-text-right has-text-success">
                                {{ $filters.currency(prorate.credit_amount, 2) }}
                            </div>
                        </div>

                        <div class="columns upgrade-plan__amount">
                            <div class="column is-9">
                                <strong>Total</strong>
                            </div>
                            <div class="column is-3 has-text-right">
                                <strong>{{ $filters.currency(prorate.prorated_amount, 2) }}</strong>
                            </div>
                        </div>

                        <div class="columns is-marginless card-footer">
                            <div class="column">
                                <small>
                                    The next charge on your {{ form.interval }} billing cycle will
                                    be on {{ $filters.date(prorate.next_charge_date) }}.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <credits-auto-recharge
                v-if="!autoRechargeStatus && !prorateLoading && isSubscription"
            />
        </template>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import Form from 'core/Form';
import CreditsAutoRecharge from '../../Broadcasts/CreditsAutoRecharge.vue';
import PlanTaxLine from './PlanTaxLine.vue';
import CardsMixin from 'mixins/cards';
import OrganizationsMixin from 'mixins/organization';
import AutoRechargeMixin from 'mixins/auto-recharge';
import ProrateMixin from 'mixins/prorate';
import NumberVendorIntegrations from 'mixins/number-vendor-integrations';
import { segmentEvent } from 'services/segment';
import { CancelToken } from 'core/api';

export default {
    emits: ['handleChangeData', 'closed'],

    components: {
        CreditsAutoRecharge,
        PlanTaxLine,
    },

    mixins: [
        CardsMixin,
        OrganizationsMixin,
        AutoRechargeMixin,
        ProrateMixin,
        NumberVendorIntegrations,
    ],

    props: {
        defaultPlan: { type: String, default: '' },
        defaultType: { type: String, default: '' },
        target: { type: String, default: '' },
        hasCard: { type: Boolean, default: false },
        price: { type: [String, Number], default: '' },
        resetPlan: { type: Boolean, default: false },
        newBilling: { type: Boolean, default: false },
        hasPlanDowngrade: { type: Boolean, default: false },
        currentPlan: { type: Object, required: true },
        defaultPlanName: { type: String, default: '' },
    },

    data() {
        return {
            prorateLoading: true,
            prorate: null,
            form: new Form(
                {
                    interval: 'yearly',
                    token: '',
                    primary: true,
                    backup: false,
                    amount: 0,
                    auto_recharge: false,
                },
                false
            ),
            stripeForm: new Form(
                {
                    name: '',
                    country: 'US',
                    city: '',
                    region: '',
                    zip: '',
                },
                false
            ),
            axiosCanceler: null,
            showCardDuringRequest: false,
        };
    },

    computed: {
        hasNotCard() {
            return Boolean(
                (!this.hasCard &&
                    (this.hasNotBillingSources ||
                        this.showCardDuringRequest ||
                        this.isCancelled ||
                        this.isSuspended ||
                        this.isUnpaid ||
                        !this.isSubscription)) ||
                    this.hasUnpaidStatusWithCard
            );
        },

        hasUnpaidStatusWithCard() {
            return Boolean(this.hasCard && this.isUnpaid);
        },

        currentDateFormatted() {
            return moment.utc(this.subscription.period_ends_at).format('MMMM D, Y');
        },

        planName() {
            if (this.newBilling) {
                return 'Pro';
            }

            switch (this.defaultPlan) {
                case 'basic-annual-2021':
                    return 'Basic';
                case 'pro-plan-monthly':
                    return 'Pro';
                case 'premium-monthly':
                    return 'Premium';
                case 'pro-plan-annual':
                    return 'Pro';
                case 'premium-annual':
                    return 'Premium';
                default:
                    return null;
            }
        },

        typeName() {
            if (this.newBilling) {
                if (this.defaultType.includes('monthly')) {
                    return 'Monthly';
                } else {
                    return 'Annual';
                }
            }

            switch (this.defaultPlan) {
                case 'pro-plan-monthly':
                case 'premium-monthly':
                    return 'Monthly';
                case 'basic-annual-2021':
                case 'pro-plan-annual':
                case 'premium-annual':
                    return 'Annual';
                default:
                    return null;
            }
        },

        planNameWithoutCard() {
            if (this.newBilling) {
                if (this.defaultType.includes('monthly')) {
                    return `Pro - $${this.price} / Monthly`;
                } else {
                    return `Pro - $${this.price} / Monthly*`;
                }
            } else {
                if (this.defaultPlan.includes('monthly')) {
                    if (this.defaultPlan.includes('pro')) {
                        return `Pro - $${this.price} / Monthly`;
                    } else {
                        return `Premium - $${this.price} / Monthly`;
                    }
                } else {
                    if (this.defaultPlan.includes('basic')) {
                        return 'Basic - $150 / Annually';
                    }
                    if (this.defaultPlan.includes('pro')) {
                        return 'Pro - $350 / Annually';
                    } else {
                        return 'Premium - $3000 / Annually';
                    }
                }
            }
        },

        ...mapState(stores.Subscriptions, ['subscription']),
    },

    watch: {
        prorateLoading(status) {
            if (!status) {
                if (!this.hasCard || this.hasUnpaidStatusWithCard) {
                    this.$nextTick(() => {
                        this.initStripe();
                        this.initStripeCard('#create-card');
                    });
                }
            }
        },
    },

    methods: {
        async calculateProrate() {
            this.axiosCanceler = CancelToken.source();

            if (!this.isSubscription) {
                this.prorateLoading = false;

                return;
            }

            try {
                this.prorateLoading = true;
                this.$emit('handleChangeData', ['form', 'busy', true]);
                this.axiosCanceler.cancel();
                this.axiosCanceler = CancelToken.source();

                const params = {
                    plan: this.defaultPlan,
                };

                this.prorate = await this.$store.dispatch('getBillingPreviewPlanMicroservice', {
                    cancelToken: this.axiosCanceler.token,
                    params: params,
                });

                this.prorateLoading = false;
                this.$emit('handleChangeData', ['form', 'busy', false]);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'calculateProrate');
                this.prorateLoading = false;
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },

        reset() {
            this.form.reset();
            this.stripeForm.reset();
            this.stripeError = null;
        },

        closed() {
            this.reset();
            this.$emit('closed', false);
        },

        async handleResetPlan() {
            try {
                this.$emit('handleChangeData', ['form', 'busy', true]);
                const response = await this.$store.dispatch('updateBillingUpgradeResetCycle');

                let credits = this.prorate.incoming_credits;

                await this.$store.dispatch('accountBalanceUpdated', {
                    account_balance: this.organization.account_balance,
                    account_credits: credits,
                    trial_credits: this.organization.trial_credits,
                });
                this.$store.dispatch('setUserData', response.data);

                this.$emit('handleChangeData', ['form', 'successful', true]);

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.closed();
                }, 1500);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'save');

                if (e.response.data.card_error) {
                    this.form.errors.record({ card_error: [e.response.data.card_error] });
                }
            } finally {
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },

        async handleExistCard() {
            if (
                this.defaultPlan === 'pro-plan-monthly' &&
                this.planId !== 'free-plan-trial' &&
                this.planId !== 'free-plan-monthly' &&
                !this.checkPlanBasic &&
                !this.newBilling
            ) {
                await this.upgradeOrganization(
                    this.form,
                    () => {},
                    () => {},
                    () => {
                        this.$emit('handleChangeData', ['form', 'successful', false]);
                    },
                    true
                );
                this.$emit('handleChangeData', ['form', 'busy', false]);
            } else {
                await this.doUpgrade();
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },

        async handleNotExistCard() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                if (!this.form.name) {
                    this.form.errors.record({ name: ['Card Holder Name is required'] });
                    this.form.busy = false;
                } else {
                    this.form.errors.clear();
                }

                const result = await this.stripe.createToken(this.stripeCard, {
                    name: `${this.stripeForm.name.trim()}`,
                    address_city: this.stripeForm.city,
                    address_state: this.stripeForm.region,
                    address_country: this.stripeForm.country,
                });

                if (result.error) throw result;
                if (!this.form.name) {
                    this.form.busy = false;
                    this.$emit('handleChangeData', ['form', 'busy', false]);

                    return;
                }

                this.form.token = result.token.id;

                await this.doUpgrade();
            } catch (e) {
                this.$emit('handleChangeData', ['form', 'busy', false]);

                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }
            }
        },

        async handleSubscriptionCanceled() {
            try {
                this.form.busy = true;
                this.stripeError = null;

                if (!this.form.name) {
                    this.form.errors.record({ name: ['Card Holder Name is required'] });
                    this.form.busy = false;
                } else {
                    this.form.errors.clear();
                }

                const result = await this.stripe.createToken(this.stripeCard, {
                    name: `${this.stripeForm.name.trim()}`,
                    address_city: this.stripeForm.city,
                    address_state: this.stripeForm.region,
                    address_country: this.stripeForm.country,
                });

                if (result.error) throw result;
                if (!this.form.name) {
                    this.form.busy = true;

                    return;
                }

                this.form.token = result.token.id;

                const params = {
                    token: this.form.token,
                    plan: this.defaultPlan,
                };

                try {
                    await this.$store.dispatch('updateBillingRestartMicroservice', { params });
                } catch (e) {
                    const error_message =
                        e.response.data?.card_error || Object.values(e.response.data)[0];
                    throw new Error(error_message);
                }

                this.$emit('handleChangeData', ['form', 'busy', false]);
                this.$emit('handleChangeData', ['form', 'successful', true]);

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.closed();
                    window.location = '/';
                }, 1500);
            } catch (e) {
                this.$emit('handleChangeData', ['form', 'busy', false]);

                if (e.error) {
                    this.stripeError = e.error.message;
                } else {
                    this.stripeError =
                        e.message || 'Failed to save your payment method, please try again.';
                }
            }
        },

        async save() {
            this.$emit('handleChangeData', ['form', 'busy', true]);

            if (this.resetPlan && !this.isCancelled) {
                this.handleResetPlan();

                return;
            }

            if (this.isCancelled) {
                this.handleSubscriptionCanceled();

                return;
            }

            if (!this.hasCard || this.hasUnpaidStatusWithCard) {
                this.showCardDuringRequest = true;
                this.handleNotExistCard();

                return;
            }

            if (this.hasCard) {
                this.handleExistCard();

                return;
            }
        },

        async doUpgrade() {
            try {
                let response = null;
                let params = null;
                let isSubscription = this.isSubscription;
                let isIncompleteStatus = ['canceled', 'unpaid', 'incomplete_expired'].includes(
                    this.subscriptionStatus
                );
                let isOnTrial = this.isOnTrial;

                if (this.newBilling) {
                    params = {
                        plan: this.defaultPlan,
                    };

                    if (this.form.token) {
                        params.token = this.form.token;
                    }

                    await this.$store.dispatch('updateBillingUpgradePlanMicroservice', { params });
                    await this.$store.dispatch('getUserMe');
                } else {
                    params = {
                        ...this.form,
                        plan: this.defaultPlan,
                    };

                    response = await this.$store.dispatch('updateBillingUpgradePlan', { params });
                    this.$store.dispatch('setUserData', response.data);
                }

                await this.$store.dispatch('accountBalanceUpdated', {
                    account_balance: this.organization.account_balance,
                    account_credits: this.organization.account_credits,
                    trial_credits: this.organization.trial_credits,
                });

                if (!isSubscription) {
                    this.showCardDuringRequest = false;
                    this.$emit('handleChangeData', ['form', 'successful', true]);

                    setTimeout(() => {
                        this.closed();
                        this.$router.replace('/register');
                    }, 1500);

                    return;
                }

                this.showCardDuringRequest = false;
                this.$emit('handleChangeData', ['form', 'successful', true]);

                if (isOnTrial) {
                    segmentEvent(
                        'Purchase',
                        {
                            value: this.target === 'monthly' ? this.price : this.price * 12,
                        },
                        {
                            context: {
                                organizationId: this.organization.id,
                            },
                        }
                    );
                }

                setTimeout(() => {
                    this.closed();
                    if (isOnTrial && !this.hasTwilioNumberVendorIntegration) {
                        this.$router.push('/settings/organization/compliance');
                    }

                    if (isIncompleteStatus) {
                        window.location = '/';
                    }
                }, 1500);
            } catch (error) {
                const data = error.response?.data || error

                this.form.errors.record(data['error'] || '');

                if (error && data.hasOwnProperty('stripe')) {
                    let subscription = cloneDeep(this.subscription);

                    subscription['status'] = 'unpaid';
                    subscription['suspended_for'] = 'failed-plan-upgrade';
                }

                setTimeout(() => {
                    this.$emit('handleChangeData', ['form', 'successful', false]);
                    this.$emit('handleChangeData', ['showError', true]);
                }, 1500);
            } finally {
                this.$emit('handleChangeData', ['form', 'busy', false]);
            }
        },
    },

    async mounted() {
        if (this.newBilling) {
            if (this.defaultType.includes('monthly')) {
                this.form.interval = 'monthly';
            } else {
                this.form.interval = 'yearly';
            }
        } else {
            if (this.defaultPlan.includes('monthly')) {
                this.form.interval = 'monthly';
            } else {
                this.form.interval = 'yearly';
            }
        }

        if (!this.hasPlanDowngrade) {
            this.calculateProrate();
        }
    },
};
</script>
